import React, { useState, useEffect } from 'react';
import { Package, Plus, Search, FileText, Trash2, Save, X, Upload, Sparkles, Loader2, Download, Paperclip, CheckCircle } from 'lucide-react';
import ClearFiltersButton from './ClearFiltersButton';
import MoneyInput from './inputs/MoneyInput';
import jsPDF from 'jspdf';

import { Product, AnalyzedFile } from '../types';
// TODO: Implement extractProductData via API call to backend
// import { extractProductData } from '../services/geminiService';
import { logActivity } from '../services/auditService';
import { apiService } from '../services/apiService';

import { useModal } from './ModalProvider';

const STORAGE_KEY = 'licitaflow_products';

const ProductManager: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [allProducts, setAllProducts] = useState<Product[]>([]); // used to build filter options
    const [searchTerm, setSearchTerm] = useState('');
    const [brandFilter, setBrandFilter] = useState<string>('ALL');
    const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<Partial<Product>>({
    name: '',
    brand: '',
    model: '',
    description: '',
    price: 0,
    category: '' // Novo campo
  });

  // File & AI State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);

    const { showAlert, showConfirm } = useModal();

    // Load all products once to populate filter selects
    useEffect(() => {
        const loadAll = async () => {
            try {
                const prods = await apiService.getProducts();
                setAllProducts(prods || []);
            } catch (e) {
                console.error('Erro ao carregar produtos para filtros:', e);
            }
        };
        loadAll();
    }, []);

    // Load products according to selected filters / search (server-side)
    useEffect(() => {
        const load = async () => {
            try {
                const params: any = {};
                if (brandFilter && brandFilter !== 'ALL') params.brand = brandFilter;
                if (categoryFilter && categoryFilter !== 'ALL') params.category = categoryFilter;
                if (searchTerm && searchTerm.trim()) params.q = searchTerm.trim();
                const prods = await apiService.getProducts(params);
                setProducts(prods || []);
            } catch (e) {
                console.error('Erro ao carregar produtos filtrados:', e);
            }
        };
        load();
    }, [brandFilter, categoryFilter, searchTerm]);

  const saveProducts = (newProducts: Product[]) => {
        setProducts(newProducts);
  };

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setForm({
        ...product,
        description: product.description || '',
        name: product.name || '',
        brand: product.brand || '',
        model: product.model || '',
        category: product.category || ''
      });
      setSelectedFile(null); // Não limpamos o datasheet existente, apenas o input de novo arquivo
    } else {
      setEditingProduct(null);
      setForm({ name: '', brand: '', model: '', description: '', price: 0, category: '' });
      setSelectedFile(null);
    }
    setAiSuccess(false);
    setIsModalOpen(true);
  };

    // Limpar filtros
    const clearFilters = () => {
        setSearchTerm('');
        setBrandFilter('ALL');
        setCategoryFilter('ALL');
    };

        const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
                    await showAlert('Por favor, envie apenas arquivos PDF.');
          return;
      }
      setSelectedFile(file);
      setAiSuccess(false); // Reset status de IA se o usuário trocar o arquivo manualmente sem pedir extração
    }
  };

    const normalize = (s?: string) => (s || '').toString().trim().toLowerCase();

  // Função Mágica: Upload + Extração
  const handleAiExtraction = async (file: File) => {
      setIsExtracting(true);

      try {
          // Chama a API para extrair dados do PDF
          const extractedData = await apiService.extractDataFromPDF(file);

          // Preenche o formulário com os dados extraídos
          setForm(prev => ({
              ...prev,
              name: extractedData.name || prev.name || file.name.replace('.pdf', '').replace(/[-_]/g, ' '),
              brand: extractedData.brand || prev.brand || '',
              model: extractedData.model || prev.model || '',
              description: extractedData.description || prev.description || ''
          }));

          setAiSuccess(true);
          await showAlert("Dados extraídos com sucesso! Revise e clique em Salvar.");
      } catch (error) {
          console.error('Erro ao extrair dados do PDF:', error);
          // Fallback: preenche apenas o nome com o nome do arquivo
          setForm(prev => ({
              ...prev,
              name: prev.name || file.name.replace('.pdf', '').replace(/[-_]/g, ' ')
          }));
          await showAlert("Não foi possível extrair dados automaticamente. Preencha os campos manualmente.");
      } finally {
          setIsExtracting(false);
      }
  };

  const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          const file = e.dataTransfer.files[0];
          if (file.type === 'application/pdf') {
              setSelectedFile(file);
              handleAiExtraction(file); // Gatilho automático ao soltar
          } else {
              await showAlert("Apenas arquivos PDF são aceitos para Datasheets.");
          }
      }
  };

    const handleSave = async () => {
        if (!form.name) {
            await showAlert('O nome do produto é obrigatório.');
            return;
        }

        try {
            // prepara objeto do produto
            const productBody: any = {
                name: form.name,
                brand: form.brand ? form.brand.toString().trim() : null,
                category: form.category ? form.category.toString().trim() : null,
                model: form.model || null,
                description: form.description || null,
                price: form.price !== undefined ? form.price : null
            };

            // se arquivo selecionado, faz upload para S3
            if (selectedFile) {
                // 1. Gerar Key única
                const fileExt = selectedFile.name.split('.').pop();
                const key = `products/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;

                // 2. Obter URL de Upload
                const { uploadUrl } = await apiService.getPresignedUploadUrl(key, selectedFile.type);

                // 3. Fazer Upload direto para o S3
                await apiService.uploadFileToS3(uploadUrl, selectedFile);

                productBody.datasheet = {
                    name: selectedFile.name,
                    type: selectedFile.type,
                    size: selectedFile.size,
                    fileUrl: key // Envia a key do S3 em vez do base64
                };
            }

            if (editingProduct) {
                const updated = await apiService.updateProduct(editingProduct.id, productBody);
                // manter state local sincronizado
                setProducts(products.map(p => (p.id === updated.id ? updated : p)));
                logActivity('PRODUTO_ATUALIZACAO', `Produto atualizado: ${form.name}`, 'INFO');
            } else {
                const created = await apiService.createProduct(productBody);
                setProducts([created, ...products]);
                logActivity('PRODUTO_CRIACAO', `Novo produto cadastrado: ${form.name}`, 'SUCCESS');
            }

            setIsModalOpen(false);
        } catch (e: any) {
            console.error('Erro ao salvar produto:', e);
            await showAlert('Erro ao salvar produto: ' + (e.message || e));
        }
    };

  const handleDelete = async (id: string, name: string) => {
        const ok = await showConfirm(`Excluir o produto "${name}"?`);
        if (!ok) return;
        try {
            await apiService.deleteProduct(id);
            const updated = products.filter(p => p.id !== id);
            setProducts(updated);
            logActivity('PRODUTO_EXCLUSAO', `Produto excluído: ${name}`, 'WARNING');
        } catch (e) {
            console.error('Erro ao deletar produto:', e);
            await showAlert('Erro ao deletar produto');
        }
  };

  const handleDownloadDatasheet = (product: Product) => {
        (async () => {
            try {
                const ds = await apiService.downloadDatasheet(product.id);

                if (ds && ds.fileUrl) {
                    const { downloadUrl } = await apiService.getPresignedDownloadUrl(ds.fileUrl);
                    window.open(downloadUrl, '_blank');
                    return;
                }

                if (!ds || !ds.base64) {
                    await showAlert('Nenhum datasheet anexado.');
                    return;
                }
                const link = document.createElement('a');
                link.href = `data:${ds.type};base64,${ds.base64}`;
                link.download = ds.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (e) {
                console.error('Erro ao baixar datasheet:', e);
                await showAlert('Erro ao baixar datasheet');
            }
        })();
  };

  const filteredProducts = products.filter(p =>
            // Search term matches name, brand or model
            (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase()) )||
            (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase()) )||
            (p.model && p.model.toLowerCase().includes(searchTerm.toLowerCase()) )
  );

    // Apply brand/category filters
    const filteredAndSearched = filteredProducts.filter(p => {
        const matchesBrand = brandFilter === 'ALL' || (p.brand || '').toLowerCase() === brandFilter.toLowerCase();
        const matchesCategory = categoryFilter === 'ALL' || (p.category || '').toLowerCase() === categoryFilter.toLowerCase();
        return matchesBrand && matchesCategory;
    });

        const exportProducts = () => {
            try {
                    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
                    const rows = products.map(p => [p.name || '-', p.brand || '-', p.category || '-', p.model || '-', p.price ? `R$ ${p.price.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '-']);

                    // Use autoTable if available (better layout), else fallback to plain text
                    if (typeof (doc as any).autoTable === 'function') {
                        (doc as any).autoTable({
                            head: [['Nome', 'Marca', 'Categoria', 'Modelo', 'Preço']],
                            body: rows,
                            startY: 60,
                            styles: { fontSize: 10 },
                            headStyles: { fillColor: [242,242,247], textColor: [51,51,51], fontStyle: 'bold' },
                            margin: { left: 40, right: 40 }
                        });
                    } else {
                        const title = 'Lista de Produtos';
                        doc.setFontSize(16);
                        doc.text(title, 40, 50);
                        doc.setFontSize(10);
                        doc.text(new Date().toLocaleString(), 40, 66);

                        let y = 90;
                        const lineHeight = 14;

                        if (products.length === 0) {
                            doc.text('Nenhum produto encontrado com os filtros selecionados.', 40, y);
                        } else {
                            products.forEach((p, idx) => {
                                if (y > 750) { doc.addPage(); y = 40; }
                                doc.setFontSize(12);
                                doc.text(`${idx + 1}. ${p.name}`, 40, y);
                                y += lineHeight;
                                doc.setFontSize(10);
                                doc.text(`Marca: ${p.brand || '-'}    Categoria: ${p.category || '-'}    Modelo: ${p.model || '-'}    Preço: ${p.price ? `R$ ${p.price.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '-'}`, 48, y);
                                y += lineHeight + 4;
                                const desc = (p.description || '').split('\n');
                                for (const dline of desc) {
                                    if (y > 750) { doc.addPage(); y = 40; }
                                    const text = dline.length > 180 ? dline.substring(0, 177) + '...' : dline;
                                    doc.text(text, 48, y);
                                    y += lineHeight;
                                }
                                y += 6;
                            });
                        }
                    }

                    doc.save(`produtos_${Date.now()}.pdf`);
            } catch (e) {
                console.error('Erro ao exportar PDF:', e);
            }
        };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
            <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Cadastro de Produtos & Datasheets
            </h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Gerencie seu catálogo de produtos e fichas técnicas para agilizar propostas.</p>
          </div>
                    <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleOpenModal()}
                                                className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Novo Produto
                                            </button>
                                            <button onClick={() => exportProducts()} className="flex items-center gap-2 px-3 py-2 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt text-sm">
                                                <Download className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                                Exportar (PDF)
                                            </button>
                                            <ClearFiltersButton onClear={clearFilters} />
                            </div>
        </div>

        <div className="mt-6 relative">
                <div className="flex gap-2 mb-3">
                <div className="w-48">
                    <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg px-3 py-2 outline-none">
                        <option value="ALL">Marca: Todas</option>
                        {(() => {
                            const map: Record<string, string> = {};
                            allProducts.forEach(p => {
                                if (p.brand) {
                                    const k = normalize(p.brand);
                                    if (!map[k]) map[k] = p.brand;
                                }
                            });
                            return Object.keys(map).map(k => <option key={k} value={k}>{map[k]}</option>);
                        })()}
                    </select>
                </div>
                <div className="w-48">
                    <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg px-3 py-2 outline-none">
                        <option value="ALL">Categoria: Todas</option>
                        {(() => {
                            const map: Record<string, string> = {};
                            allProducts.forEach(p => {
                                if (p.category) {
                                    const k = normalize(p.category);
                                    if (!map[k]) map[k] = p.category;
                                }
                            });
                            return Object.keys(map).map(k => <option key={k} value={k}>{map[k]}</option>);
                        })()}
                    </select>
                </div>
            </div>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                    type="text"
                    placeholder="Buscar por nome, marca ou modelo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
            </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSearched.map(product => (
              <div key={product.id} className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark hover:shadow-md transition-all p-5 flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-1 flex-wrap">
                        <span className="text-[10px] font-bold text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt px-2 py-0.5 rounded uppercase">
                            {product.brand}
                        </span>
                        {product.category && (
                            <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded uppercase">
                                {product.category}
                            </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                          <button onClick={() => handleOpenModal(product)} className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-blue-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-blue-50">
                              <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(product.id, product.name)} className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                          </button>
                      </div>
                  </div>

                  <h3 className="font-semibold text-ink-title dark:text-ink-title-dark mb-1 truncate" title={product.name}>{product.name}</h3>
                  <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-3 truncate">{product.model}</p>

                  <div className="flex-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt p-2 rounded border border-line dark:border-line-dark mb-3 line-clamp-3">
                      {product.description || 'Sem descrição.'}
                  </div>

                  <div className="mt-auto flex items-center justify-between pt-3 border-t border-line dark:border-line-dark">
                      <span className="font-bold text-ink-title dark:text-ink-title-dark text-sm">
                          {product.price ? `R$ ${product.price.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '-'}
                      </span>
                      {product.datasheet ? (
                          <button
                              onClick={() => handleDownloadDatasheet(product)}
                              className="text-xs flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline"
                          >
                              <Paperclip className="w-3 h-3" /> Datasheet
                          </button>
                      ) : (
                          <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark italic">Sem datasheet</span>
                      )}
                  </div>
              </div>
          ))}
      </div>

      {filteredAndSearched.length === 0 && (
          <div className="text-center py-12 text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark border-dashed">
              <Package className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark mx-auto mb-3" />
              <p>Nenhum produto encontrado.</p>
          </div>
      )}

      {/* Modal */}
      {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                  <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                      <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                          <Package className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                      </h3>
                      <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                          <X className="w-5 h-5" />
                      </button>
                  </div>

                  <div className="p-6 overflow-y-auto space-y-6">

                      {/* Área de Criação via IA */}
                      {!editingProduct && (
                          <div
                              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer relative overflow-hidden group ${
                                  aiSuccess ? 'border-green-300 bg-green-50 dark:bg-green-900/30' : 'border-purple-300 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100'
                              }`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={onDropFile}
                          >
                              {isExtracting ? (
                                  <div className="flex flex-col items-center justify-center py-4">
                                      <Loader2 className="w-8 h-8 text-purple-600 dark:text-purple-300 animate-spin mb-2" />
                                      <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Analisando Datasheet com IA...</p>
                                      <p className="text-xs text-purple-600 dark:text-purple-300">Extraindo especificações técnicas</p>
                                  </div>
                              ) : aiSuccess ? (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300 mb-2" />
                                      <p className="text-sm font-bold text-green-800 dark:text-green-200">Dados Extraídos com Sucesso!</p>
                                      <p className="text-xs text-green-600 dark:text-green-300">O formulário abaixo foi preenchido automaticamente.</p>
                                  </div>
                              ) : (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <div className="bg-surface dark:bg-surface-dark p-3 rounded-full shadow-sm mb-3">
                                          <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                                      </div>
                                      <h4 className="font-bold text-purple-900 mb-1">Preenchimento Automático via IA</h4>
                                      <p className="text-xs text-purple-700 max-w-xs mx-auto mb-3">
                                          Arraste o PDF do Datasheet aqui para que a IA preencha o cadastro e anexe o arquivo automaticamente.
                                      </p>
                                      <label className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer shadow-sm">
                                          Selecionar PDF
                                          <input
                                              type="file"
                                              accept=".pdf"
                                              className="hidden"
                                              onChange={(e) => {
                                                  if (e.target.files && e.target.files[0]) {
                                                      setSelectedFile(e.target.files[0]);
                                                      handleAiExtraction(e.target.files[0]);
                                                  }
                                              }}
                                          />
                                      </label>
                                  </div>
                              )}
                          </div>
                      )}

                      <div className="space-y-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Nome do Produto *</label>
                              <input
                                  type="text"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  placeholder="Ex: Notebook Latitude 3420"
                                  value={form.name}
                                  onChange={e => setForm({...form, name: e.target.value})}
                              />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Marca</label>
                                  <input
                                      type="text"
                                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                      placeholder="Ex: Dell"
                                      value={form.brand}
                                      onChange={e => setForm({...form, brand: e.target.value})}
                                  />
                              </div>
                              <div>
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Modelo / Part Number</label>
                                  <input
                                      type="text"
                                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                      placeholder="Ex: 210-AYVO"
                                      value={form.model}
                                      onChange={e => setForm({...form, model: e.target.value})}
                                  />
                              </div>
                          </div>

                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Descrição Técnica Resumida</label>
                              <textarea
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none h-24"
                                  placeholder="Principais specs..."
                                  value={form.description}
                                  onChange={e => setForm({...form, description: e.target.value})}
                              />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Preço de Referência (R$)</label>
                                  <MoneyInput
                                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                      value={typeof form.price === 'number' ? form.price : null}
                                      onValueChange={(v) => setForm({...form, price: v ?? 0})}
                                  />
                              </div>
                              <div>
                                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Anexo (Datasheet PDF)</label>
                                  <input
                                      type="file"
                                      accept=".pdf"
                                      onChange={handleFileUpload}
                                      className="block w-full text-sm text-ink-subtitle dark:text-ink-subtitle-dark file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
                                  />
                                  {editingProduct?.datasheet && !selectedFile && (
                                      <p className="text-[10px] text-green-600 dark:text-green-300 mt-1 flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" /> Arquivo atual: {editingProduct.datasheet.name}
                                      </p>
                                  )}
                                  {selectedFile && (
                                      <p className="text-[10px] text-blue-600 dark:text-blue-300 mt-1 flex items-center gap-1">
                                          <Upload className="w-3 h-3" /> Novo arquivo selecionado: {selectedFile.name}
                                      </p>
                                  )}
                              </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Categoria</label>
                            <input
                                type="text"
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                placeholder="Ex: Informática, Relógio de Ponto"
                                value={form.category || ''}
                                onChange={e => setForm({...form, category: e.target.value})}
                            />
                          </div>
                      </div>
                  </div>

                  <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                      <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">
                          Cancelar
                      </button>
                      <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm">
                          <Save className="w-4 h-4" />
                          Salvar Produto
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default ProductManager;
